/**
 * Shared screenshot capture + compression. Extracted from ReportButton so the 🐞 Report flow and the 📋
 * Delegate-task flow use ONE implementation (no drift). Capture renders the live DOM via modern-screenshot
 * (so modern CSS works), crops to the visible viewport, and compresses to a JPEG data: URL.
 */
import { domToCanvas } from 'modern-screenshot';

/** Downscale + recompress a data: URL so the upload reliably clears the server body limit AND any
 *  front-proxy body cap. A retina full-page capture is several MB of base64 (a 413/400 → "Couldn't
 *  send"); this caps the longest edge and drops quality, yielding ~50–200KB. Falls back to the
 *  original on any decode error. */
export function shrinkDataUrl(dataUrl: string, maxEdge = 1400, quality = 0.6): Promise<string> {
  return new Promise(resolve => {
    const im = new Image();
    im.onload = () => {
      const scale = Math.min(1, maxEdge / Math.max(im.width, im.height));
      const w = Math.max(1, Math.round(im.width * scale)), h = Math.max(1, Math.round(im.height * scale));
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      if (!ctx) { resolve(dataUrl); return; }
      ctx.drawImage(im, 0, 0, w, h);
      resolve(c.toDataURL('image/jpeg', quality));
    };
    im.onerror = () => resolve(dataUrl);
    im.src = dataUrl;
  });
}

/** Capture the VISIBLE viewport ("what you're looking at") as a JPEG data: URL. Render the body, then crop
 *  to the scroll offset so it's crisp, not the whole shrunk page. Returns null on any capture error (the
 *  caller can still proceed text-only). Call this BEFORE opening a modal so the modal isn't in the shot. */
export async function captureViewport(): Promise<string | null> {
  // html.is-capturing (styles/tokens.css) pauses animations/transitions and hides toasts +
  // confetti so modern-screenshot renders a stable DOM mid-animation.
  document.documentElement.classList.add('is-capturing');
  try {
    const scale = Math.min(window.devicePixelRatio || 1, 2);
    const full = await domToCanvas(document.body, { scale });
    const sx = window.scrollX * scale, sy = window.scrollY * scale;
    const sw = Math.max(1, Math.min(window.innerWidth * scale, full.width - sx));
    const sh = Math.max(1, Math.min(window.innerHeight * scale, full.height - sy));
    const crop = document.createElement('canvas');
    crop.width = Math.round(sw); crop.height = Math.round(sh);
    crop.getContext('2d')!.drawImage(full, sx, sy, sw, sh, 0, 0, crop.width, crop.height);
    return crop.toDataURL('image/jpeg', 0.85);
  } catch (e) {
    console.warn('screenshot capture failed — text-only still works', e);
    return null;
  } finally {
    document.documentElement.classList.remove('is-capturing');
  }
}

/** OS-level screen capture fallback (getDisplayMedia) for cases the DOM render can't handle. Returns a JPEG
 *  data: URL, or null if the user cancels the picker. */
export async function captureScreen(): Promise<string | null> {
  try {
    const md = navigator.mediaDevices as any;
    const stream = await md.getDisplayMedia({ video: true });
    const video = document.createElement('video');
    video.srcObject = stream; await video.play();
    await new Promise(r => setTimeout(r, 300));
    const tmp = document.createElement('canvas');
    tmp.width = video.videoWidth; tmp.height = video.videoHeight;
    tmp.getContext('2d')!.drawImage(video, 0, 0);
    stream.getTracks().forEach((t: any) => t.stop());
    return tmp.toDataURL('image/jpeg', 0.8);
  } catch {
    return null;
  }
}
